<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import type { HistoryDay, UptimeBand } from '@/types';
import { dayBand, downtimeMinutes, formatUptime, uptimeBandClass, uptimeBandLabel } from '@/utils/status';
import { statusLocale, statusMessages } from '@/i18n';

const props = defineProps<{
  componentId: string;
  history: HistoryDay[];
  windowDays: number;
  /** Formatted window uptime, shown in the header beside the title. */
  uptime: string;
}>();

interface DayCell {
  date: string;
  band: UptimeBand;
  title: string;
}

/**
 * Days actually rendered.
 *
 * 90 cells inside a narrow column collapse into unreadable 1px slivers, so the
 * bar drops older days until each cell has room to be seen and hovered. Starts
 * at the full window and narrows once the real width is measured.
 */
const MIN_CELL_PX = 5;
const MIN_DAYS = 14;

const bar = ref<HTMLElement | null>(null);
const availableWidth = ref(0);
let observer: ResizeObserver | null = null;

onMounted(() => {
  if (bar.value === null || typeof ResizeObserver === 'undefined') return;
  observer = new ResizeObserver((entries) => {
    availableWidth.value = entries[0]?.contentRect.width ?? 0;
  });
  observer.observe(bar.value);
});

onBeforeUnmount(() => {
  observer?.disconnect();
  observer = null;
});

const visibleDays = computed(() => {
  if (availableWidth.value === 0) return props.windowDays;
  const fits = Math.floor(availableWidth.value / MIN_CELL_PX);
  return Math.max(MIN_DAYS, Math.min(props.windowDays, fits));
});

const dateFormatter = new Intl.DateTimeFormat(statusLocale, {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC',
});

const cells = computed<DayCell[]>(() => {
  const byDate = new Map<string, HistoryDay>();
  for (const entry of props.history) {
    if (entry.componentId === props.componentId) {
      byDate.set(entry.date, entry);
    }
  }

  const now = new Date();
  const today = new Date(now);
  today.setUTCHours(0, 0, 0, 0);

  const result: DayCell[] = [];
  for (let offset = visibleDays.value - 1; offset >= 0; offset--) {
    const day = new Date(today);
    day.setUTCDate(day.getUTCDate() - offset);
    const iso = day.toISOString().slice(0, 10);
    const entry = byDate.get(iso);
    const band = entry ? dayBand(entry.uptime, entry.status) : 'nodata';
    const label = dateFormatter.format(day);

    result.push({ date: iso, band, title: cellTitle(entry, band, label, now) });
  }

  return result;
});

function cellTitle(
  entry: HistoryDay | undefined,
  band: UptimeBand,
  label: string,
  now: Date,
): string {
  if (entry === undefined || band === 'nodata') {
    return statusMessages.uptimeCellNoData(label);
  }

  const base = statusMessages.uptimeCellTitle(
    label,
    uptimeBandLabel(band),
    formatUptime(entry.uptime ?? null),
  );

  if (entry.uptime === null || entry.uptime === undefined || entry.uptime >= 1) return base;
  const minutes = downtimeMinutes(entry.uptime, entry.date, now);
  return minutes > 0 ? `${base} · ${statusMessages.uptimeDowntime(minutes)}` : base;
}
</script>

<template>
  <div class="uptime">
    <div class="uptime-head">
      <span class="uptime-title">{{ statusMessages.uptimeTitle(visibleDays) }}</span>
      <span class="uptime-current">{{ uptime }}</span>
    </div>

    <div class="uptime-plot">
      <div
        ref="bar"
        class="uptime-bar"
        role="img"
        :aria-label="statusMessages.uptimeAria(visibleDays)"
      >
        <span
          v-for="cell in cells"
          :key="cell.date"
          class="uptime-cell"
          :class="uptimeBandClass(cell.band)"
          :title="cell.title"
        ></span>
      </div>
      <div class="uptime-scale" aria-hidden="true">
        <span>{{ statusMessages.uptimeRangeStart(visibleDays) }}</span>
        <span>{{ statusMessages.uptimeToday }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.uptime {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
}

.uptime-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.uptime-title {
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: var(--xt-fg-muted);
}

.uptime-current {
  font-size: 0.7rem;
  color: var(--xt-fg-muted);
  font-variant-numeric: tabular-nums;
}

/* The gutter keeps the bar flush with the response-time plot, which gives up the
   same strip to its y-axis. Without it the two charts would sit on different
   right edges and read as unrelated widths. */
.uptime-plot {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
  margin-right: var(--xt-chart-gutter);
}

.uptime-bar {
  display: flex;
  gap: 1px;
  height: 28px;
  align-items: stretch;
}

.uptime-cell {
  flex: 1 1 0;
  min-width: 0;
  border-radius: 1px;
  background: var(--xt-uptime-band);
  transition: transform 0.1s ease;
}

.uptime-cell:hover {
  transform: scaleY(1.12);
}

.uptime-scale {
  display: flex;
  justify-content: space-between;
  font-size: 0.7rem;
  color: var(--xt-fg-muted);
}

.band-ok {
  --xt-uptime-band: var(--xt-uptime-ok);
}

.band-nominal {
  --xt-uptime-band: var(--xt-uptime-nominal);
}

.band-degraded {
  --xt-uptime-band: var(--xt-uptime-degraded);
}

.band-partial {
  --xt-uptime-band: var(--xt-uptime-partial);
}

.band-major {
  --xt-uptime-band: var(--xt-uptime-major);
}

.band-nodata {
  --xt-uptime-band: var(--xt-uptime-nodata);
}

@media (prefers-reduced-motion: reduce) {
  .uptime-cell {
    transition: none;
  }

  .uptime-cell:hover {
    transform: none;
  }
}
</style>
